/**
 * El nombre de la persona logueada.
 *
 * Vivía como función privada de `altaDesdeCuestionario` y se movió acá cuando el
 * certificado de Atlas Academy necesitó el mismo dato
 * (ADR-2026-08-25-nombre-del-usuario-compartido): duplicar la lógica habría dejado dos
 * lugares decidiendo de dónde sale el nombre, y el día que se agregue una fuente
 * —Apple, un campo en `profiles`— uno de los dos se olvida.
 */

import { supabase } from './supabase';

/** Lo mínimo que hace falta de un usuario de auth para sacarle el nombre. */
export interface UsuarioConNombre {
  email?: string | null;
  user_metadata?: Record<string, unknown> | null;
}

/**
 * Nombre de la persona. El trigger `handle_new_user` sólo copia id/email/ref_code a
 * `profiles`, así que el nombre hay que buscarlo en la metadata del usuario de auth:
 * `full_name` lo escribe nuestro form de registro y `name` lo trae Google. Si no hay
 * ninguno (registro sin completar el campo), se usa la parte local del mail — nunca
 * queda vacío, porque el saludo de la app, Brevo y el certificado lo usan.
 */
export function nombreDelUsuario(user: UsuarioConNombre): string {
  const meta = user.user_metadata ?? {};
  const candidatos = [meta.full_name, meta.name, meta.nombre];
  for (const c of candidatos) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  const local = (user.email ?? '').split('@')[0];
  return local || 'Usuario';
}

/** El nombre del usuario de la sesión actual, o `null` si no hay sesión. */
export async function nombreDelUsuarioActual(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user ? nombreDelUsuario(user) : null;
}
